import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { SparkScrollableGridContainer } from 'sharedComponents/UI';
import AuthenticationModal from 'components/SparkRuns/SparkRunPageForm/AuthenticationModal';
import { SPARK_ROUTES } from 'constants/routes';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useCurrentUser, useDialog } from 'hooks/common';
import { BreathingSparkAnimation } from 'components/Animations';
import MemoryChat from './MemoryChat';
import ChatMessageBubble, {
  FooterActions,
} from 'components/common/Chats/ChatMessageBubble';
import { useChatMessages } from 'hooks/chats/useChatMessages';
import { useUpdateChatMessage } from 'hooks/chats/useUpdateChatMessage';
import ChatsPluginDrawer from 'components/common/Chats/ChatsPluginDrawer';
import { FunctionCall } from 'types/Chats/ChatMessage';

const MemoizedChatMessage = React.memo(ChatMessageBubble);

type SessionMemorySearchResult = {
  memorySearchID: string;
  question: string;
  answer: string;
  isLoading: boolean;
  date: string;
  memorySearchResultID: string;
};

type MemoryEditContentProps = {
  memoryID: string;
  activeMemoryChatID: string;
};

function formatName(name: string): string {
  return name
    .replace(/\d/g, '')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function MemoryEditContent({
  memoryID,
  activeMemoryChatID,
}: MemoryEditContentProps) {
  const navigate = useNavigate();
  const { getValue, removeItem } = useLocalStorage();
  const { applicationUser, currentUserID } = useCurrentUser();
  const [sessionUserQAs, setSessionUserQAs] = React.useState<
    SessionMemorySearchResult[]
  >([]);
  const [chatMessagePlugins, setChatMessagePlugins] =
    React.useState<FunctionCall>();
  const { mutateAsync: updateChatMessage } = useUpdateChatMessage(
    undefined,
    activeMemoryChatID
  );

  const {
    open: isOpenPluginInfoDrawer,
    openDialog: openPluginInfoDialog,
    closeDialog: closePluginInfoDialog,
  } = useDialog(false);
  const { chatMessages } = useChatMessages(activeMemoryChatID);

  const { open: isLoginDialogOpen, closeDialog: closeLoginDialog } =
    useDialog();

  React.useEffect(() => {
    const askedQuestion = getValue<string>(memoryID);
    if (currentUserID && askedQuestion) {
      removeItem(memoryID);
    }
  }, [currentUserID, getValue, memoryID, removeItem]);

  React.useEffect(() => {
    const lastAnswerElement = document.getElementById('recent');
    if (lastAnswerElement)
      lastAnswerElement?.scrollIntoView({ behavior: 'smooth' });
  }, [sessionUserQAs]);

  React.useEffect(() => {
    return () => {
      setSessionUserQAs([]);
    };
  }, []);

  const handleActionClick = (
    type: FooterActions,
    messageID: string,
    secondaryID?: string
  ) => {
    updateChatMessage({
      messageID,
      chatMessageRatingTypeID: Number(secondaryID),
    });
    switch (type) {
      case 'like':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'dislike':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'duplicate':
        break;
    }
  };

  const handlePluginClick = (pluginsUsed: FunctionCall) => {
    setChatMessagePlugins(pluginsUsed);
    openPluginInfoDialog();
  };

  return (
    <>
      <SparkScrollableGridContainer
        scrollableContainerStyle={{ justifyContent: 'center' }}
        actions={<MemoryChat memoryID={memoryID} chatID={activeMemoryChatID} />}
      >
        {chatMessages?.length === 0 ? (
          <Grid
            container
            height='100%'
            justifyContent='center'
            alignItems='center'
          >
            <Box
              display='flex'
              alignItems='center'
              flexDirection='column'
              gap={1}
            >
              <BreathingSparkAnimation width={100} height={100} />
              <Typography
                color={(theme) => theme.palette.grey[800]}
                variant='h6'
                fontFamily='Mont-semibold'
              >
                Ask a question{' '}
                {formatName(applicationUser?.attributes?.name || '')}
              </Typography>
            </Box>
          </Grid>
        ) : (
          <Grid container gap={2} maxWidth='800px' justifySelf='center'>
            {chatMessages?.map((message, index, messages) => (
              <MemoizedChatMessage
                id={message.id}
                key={message.id}
                message={message.attributes.content}
                isUserMessage={message.attributes.authorRoleID === 1}
                isStreaming={message.attributes.isStreaming || false}
                isLastMessage={messages.length === index + 1}
                error={message.attributes.error}
                chatMessageRatingTypeID={
                  message.attributes.chatMessageRatingTypeID
                }
                onActionClick={handleActionClick}
                authorRoleID={message.attributes.authorRoleID}
                isExpandedViewOpen={true}
                PluginsUsed={message.attributes.functionCalls}
                handlePluginClicked={handlePluginClick}
                pluginsPill={message.attributes.pluginsPill}
              />
            ))}
          </Grid>
        )}
        {isLoginDialogOpen && (
          <AuthenticationModal
            open={isLoginDialogOpen}
            onClose={closeLoginDialog}
            callBack={() => {
              navigate(`${SPARK_ROUTES.MEMORIES}/${memoryID}`);
            }}
          />
        )}
      </SparkScrollableGridContainer>
      {isOpenPluginInfoDrawer && (
        <ChatsPluginDrawer
          open={isOpenPluginInfoDrawer}
          onClose={closePluginInfoDialog}
          chatMessagePlugins={[chatMessagePlugins]}
          top='6rem'
        />
      )}
    </>
  );
}

export default MemoryEditContent;
